import React, { FunctionComponent, useState } from "react"
import { useStripe, useElements, PaymentElement } from "@stripe/react-stripe-js"
import { CartStructure } from "../../interfaces/SaleorTypes"
import styled from "styled-components"
import { HorizontalSpacer } from "../../atoms/spacers"
import { CreditCardIcon } from "../../atoms/icons/CreditCardIcon"
import { useNavigate } from "react-router-dom"
import H4 from "../../atoms/typography/H4"
import BodyLarge from "../../atoms/typography/BodyLarge"
import { Link } from "react-router-dom"
import TextButton from "../../atoms/buttons/TextButton"
import LoadingSpinner from "../../atoms/LoadingSpinner"
import { useCheckoutCompleteMutation } from "../../../hooks-saleor/checkoutCompleteMutation"
import { pushPurchaseEvent } from "../../../utils/datalayer"
import { ShareIcon } from "../../atoms/icons/ShareIcon"
import SmallColorfulButton from "../../atoms/buttons/SmallColorfulButton"
import { captureExceptionWithContext } from "../../../utils/sentry"
import { IS_USING_STRIPE_PLUGIN_GATEWAY } from "../../../utils/saleor-constants"

interface CommonStripePaymentProps {
  cart?: CartStructure[]
  checkoutId: string
  onClearCart: () => void
}

interface StripeAppPaymentProps extends CommonStripePaymentProps {
  transactionId: string
}

const CheckboxInput = styled.input`
  &[type="checkbox"] {
    position: relative;
    border: 2px solid #000;
    border-radius: 2px;
    background: none;
    cursor: pointer;
    line-height: 0;
    margin: 0 0.6em 0 0;
    outline: 0;
    padding: 0 !important;
    vertical-align: text-top;
    height: 25px;
    width: 25px;
    -webkit-appearance: none;
    opacity: 0.5;
  }

  &[type="checkbox"]:hover {
    opacity: 1;
  }

  &[type="checkbox"]:checked {
    background-color: #000;
    opacity: 1;
  }

  &[type="checkbox"]:before {
    content: "";
    position: absolute;
    right: 50%;
    top: 50%;
    width: 4px;
    height: 10px;
    border: solid #fff;
    border-width: 0 2px 2px 0;
    margin: -1px -1px 0 -1px;
    transform: rotate(45deg) translate(-50%, -50%);
    z-index: 2;
  }
`

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
`

const IconContainer = styled.div`
  display: flex;
  align-items: center;
  column-gap: 10px;
`

function assertTrueSentry(value: boolean, message: string) {
  if (!value) {
    captureExceptionWithContext(new Error("assertTrueSentry failed"), message)
  }
}

const CommonStripePayment: FunctionComponent<CommonStripePaymentProps & { transactionId?: string }> = ({
  cart,
  checkoutId,
  onClearCart,
  transactionId,
}) => {
  const CHECKOUT_COMPLETE_RETRY_LIMIT = 10
  const stripe = useStripe()
  const elements = useElements()
  const [errorMessage, setErrorMessage] = useState<String | undefined>()
  const [acceptedTerms, setAcceptedTerms] = React.useState(false)
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const { checkoutCompleteMutation } = useCheckoutCompleteMutation()

  async function tryCheckoutComplete(checkoutId: string, transactionId: string, tries: number = 0) {
    if (tries < CHECKOUT_COMPLETE_RETRY_LIMIT) {
      checkoutCompleteMutation({
        variables: {
          checkoutId: checkoutId,
        },
        onCompleted: (data) => {
          if (data.checkoutComplete.order) {
            pushPurchaseEvent(transactionId, data.checkoutComplete.order, cart)
            setLoading(false)
            onClearCart()
            navigate(`/store/confirmation?checkout_id=${checkoutId}`)
          } else {
            setTimeout(() => {
              tryCheckoutComplete(checkoutId, transactionId, tries + 1)
            }, 500)
          }
        },
      })
    } else {
      let checkoutError = new Error(`Error completing checkout: ${checkoutId}`)
      captureExceptionWithContext(
        checkoutError,
        `could not complete checkout: ${checkoutId} after ${CHECKOUT_COMPLETE_RETRY_LIMIT} tries`,
      )
      setErrorMessage("Error completing checkout. Please contact support@satcube.com.")
      setLoading(false)
    }
  }

  const handleSubmit = async (event: any) => {
    event.preventDefault()

    setErrorMessage(undefined)

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return
    }

    setLoading(true)
    const { error } = await stripe.confirmPayment({
      //`Elements` instance that was used to create the Payment Element
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/store/confirmation?checkout_id=${checkoutId}`,
      },
      redirect: "if_required",
    })

    if (error) {
      // This point will only be reached if there is an immediate error when
      // confirming the payment. Show error to your customer (for example, payment
      // details incomplete)
      if (error.message) {
        setLoading(false)
        setErrorMessage(error.message)
      }
    } else {
      if (transactionId) {
        // this is a Stripe App payment
        assertTrueSentry(
          IS_USING_STRIPE_PLUGIN_GATEWAY,
          "transactionId is unexpectedly defined while we're using Stripe Plugin gateway",
        )
        tryCheckoutComplete(checkoutId, transactionId)
      } else {
        // this is a Stripe Plugin payment
        assertTrueSentry(
          !IS_USING_STRIPE_PLUGIN_GATEWAY,
          "transactionId is unexpectedly undefined while we're using Stripe App gateway",
        )
        checkoutCompleteMutation({
          variables: {
            checkoutId: checkoutId,
          },
          onCompleted: (data) => {
            if (data.checkoutComplete.order) {
              pushPurchaseEvent(checkoutId, data.checkoutComplete.order, cart)
              setLoading(false)
              onClearCart()
              navigate(`/store/confirmation?checkout_id=${checkoutId}`)
            } else {
              onClearCart()
              navigate(`/store/confirmation?checkout_id=${checkoutId}`)
            }
          },
        })
      }
    }
  }

  return (
    <form onSubmit={handleSubmit}>
      <H4>Payment</H4>
      <HorizontalSpacer spacing={2} />
      <IconContainer>
        <CreditCardIcon />
        <BodyLarge>Credit card</BodyLarge>
      </IconContainer>
      <HorizontalSpacer spacing={2} />
      <PaymentElement />
      <HorizontalSpacer spacing={2} />
      <CheckboxContainer>
        <CheckboxInput
          type="checkbox"
          onChange={() => setAcceptedTerms(!acceptedTerms)}
          checked={acceptedTerms}
          required
        />
        <Link to="/sales-terms" target="_blank" rel="noopener noreferrer">
          <TextButton title="I have read and understood the terms and conditions" icon={<ShareIcon />} />
        </Link>
      </CheckboxContainer>
      <HorizontalSpacer spacing={2} />
      {stripe && <SmallColorfulButton title={"Confirm purchase"} disabled={loading} />}
      {loading && (
        <div>
          <LoadingSpinner />
          <div> Handling payment. Do not close this window!</div>
        </div>
      )}
      {/* Show error message to your customers */}
      {errorMessage && (
        <div>
          <HorizontalSpacer spacing={2} />
          <BodyLarge id="stripe-checkout-error-message">{errorMessage}</BodyLarge>
        </div>
      )}
    </form>
  )
}

export const StripeAppPayment: FunctionComponent<StripeAppPaymentProps> = (props) => <CommonStripePayment {...props} />
export const StripePluginPayment: FunctionComponent<CommonStripePaymentProps> = (props) => (
  <CommonStripePayment {...props} />
)
